/**
 * The part of a streamed answer that can be shown now (spec §5,
 * chat-runs-api.md §9). Deltas are cut anywhere, so the end of the text so
 * far can be half a tag (`<li cla`), half an entity (`&am`) or half a
 * citation (`[1`); those wait for the next delta. Inside an open table, a
 * row is only shown once it is closed, and a table without a closed row is
 * not shown at all, so rows appear whole.
 */

/** `<` starts markup when followed by a letter, `/`, `!` or `?`. */
const TAG_START = /<(?=[A-Za-z/!?]|$)/g;

const lastTagStart = (text: string): number => {
  let last = -1;
  TAG_START.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_START.exec(text)) !== null) {
    last = match.index;
    if (match[0].length === 0) TAG_START.lastIndex++;
  }
  return last;
};

/** Cut an unterminated tag, comment, entity or citation at the end. */
const cutIncompleteEnd = (text: string): string => {
  let out = text;
  const tagStart = lastTagStart(out);
  if (tagStart !== -1) {
    const rest = out.slice(tagStart);
    const unterminated = rest.startsWith('<!--')
      ? !rest.includes('-->')
      : !rest.includes('>');
    if (unterminated) out = out.slice(0, tagStart);
  }
  out = out.replace(/&#?[A-Za-z0-9]{0,32}$/, '');
  out = out.replace(/\[\d{0,4}$/, '');
  return out;
};

const lastIndexOfTag = (lower: string, tag: string, from?: number) => {
  const pattern = new RegExp(`<${tag}(?=[\\s>/])`, 'g');
  let last = -1;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(lower)) !== null) {
    if (from !== undefined && match.index < from) continue;
    last = match.index;
  }
  return last;
};

/** Hold back the unclosed row of an open table (and an empty open table). */
const cutOpenTableRow = (text: string): string => {
  const lower = text.toLowerCase();
  const tableStart = lastIndexOfTag(lower, 'table');
  if (tableStart === -1) return text;
  if (lower.indexOf('</table', tableStart) !== -1) return text;

  let out = text;
  const rowStart = lastIndexOfTag(lower, 'tr', tableStart);
  if (rowStart !== -1 && lower.indexOf('</tr', rowStart) === -1) {
    out = text.slice(0, rowStart);
  }
  if (out.toLowerCase().indexOf('</tr', tableStart) === -1) {
    out = text.slice(0, tableStart);
  }
  return out;
};

export const streamingPrefix = (text: string): string =>
  cutOpenTableRow(cutIncompleteEnd(text));
